/**
 * QuickServicesStrip — three premium service cards.
 *
 * Mobile: horizontal swipe carousel (one card ~85% viewport wide — matches
 *   the reference proportions where the vehicle image dominates each card).
 * sm+:    three columns side-by-side (same reference layout).
 *
 * Text contents come from the admin-editable UI texts settings so you can
 * tweak every label from the admin dashboard without touching code.
 */
import FreeDeliveryDialog from "@/components/FreeDeliveryDialog";
import { useEffect, useState } from "react";
import { ArrowLeft, Zap, Car, CarTaxiFront, Bike } from "lucide-react";
import { useUIText } from "@/lib/uiTexts";
import { api, mediaSrc } from "@/lib/api";


function ArrowCircle({ color }) {
  return (
    <span
      className="inline-flex items-center justify-center size-8 sm:size-9 rounded-full ring-2 ring-white/90 shadow-xl shrink-0"
      style={{ background: color }}
    >
      <ArrowLeft className="size-4 text-white rtl:rotate-180" strokeWidth={3} />
    </span>
  );
}


function ServiceCard({ gradient, blobTint, arrowColor, title, subtitle, discount, image, Icon, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group relative overflow-hidden rounded-[22px] shadow-xl aspect-[3/4] text-right transition-transform duration-200 hover:-translate-y-1 active:scale-[0.98] ring-1 ring-white/20"
      style={{ background: gradient }}
    >
      {/* Lighting blobs */}
      <div className="pointer-events-none absolute -top-4 end-[-20px] size-28 rounded-full blur-3xl opacity-80" style={{ background: blobTint }} />
      <div className="pointer-events-none absolute -bottom-16 start-[-30px] size-44 rounded-full blur-3xl opacity-55 bg-white/15" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tl from-black/30 via-transparent to-white/15" />

      {/* Title area */}
      <div className="absolute top-3 end-3 start-3 z-10 text-right space-y-0.5">
        <div className="text-base sm:text-lg font-black text-white leading-none drop-shadow-lg tracking-tight">
          {title}
        </div>
        <div className="text-[10px] sm:text-xs font-semibold text-white/95 leading-snug drop-shadow line-clamp-2">
          {subtitle}
        </div>
        {discount && (
          <div className="pt-1">
            {discount}
          </div>
        )}
      </div>

      {/* Admin-uploaded image (version-busted URL) or a clean icon fallback */}
      {image ? (
        <img
          src={mediaSrc(image)}
          alt=""
          decoding="async"
          className="absolute bottom-[-4px] start-[-6px] h-[55%] w-auto max-w-[85%] object-contain object-left-bottom drop-shadow-[0_10px_18px_rgba(0,0,0,0.55)] group-hover:scale-[1.04] transition-transform duration-300 pointer-events-none select-none"
          data-testid={`${testId}-img`}
        />
      ) : (
        <span className="absolute bottom-3 start-3 size-12 sm:size-14 rounded-2xl bg-white/20 ring-1 ring-white/40 backdrop-blur-md flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300" data-testid={`${testId}-icon`}>
          <Icon className="size-6 sm:size-7 text-white" strokeWidth={2.2} />
        </span>
      )}

      {/* Arrow bottom-right */}
      <div className="absolute bottom-2.5 end-2.5 z-10">
        <ArrowCircle color={arrowColor} />
      </div>
    </button>
  );
}


export default function QuickServicesStrip({
  onInternalClick,
  onExternalClick,
}) {
  // Admin-editable labels (fall back to defaults so site works before the
  // admin ever touches them).
  const titleHeader = useUIText("home.services.header", "خدمات سريعة");
  const title_ext   = useUIText("home.services.external.title",    "تكسي خارجي");
  const sub_ext     = useUIText("home.services.external.subtitle", "إلى جميع المحافظات");
  const title_int   = useUIText("home.services.internal.title",    "تكسي داخلي");
  const sub_int     = useUIText("home.services.internal.subtitle", "تنقل داخل خانقين بسعر شفاف");
  const title_free  = useUIText("home.services.free.title",        "طلب دلفري حر");
  const sub_free    = useUIText("home.services.free.subtitle",     "اطلب الآن من مطاعم مدينتك");
  const [imgs, setImgs] = useState({});
  useEffect(() => { api.get("/taxi/settings").then(r => setImgs(r.data || {})).catch(() => {}); }, []);

  return (
    <section className="mx-auto max-w-md px-4 mt-6" data-testid="quick-services-strip">
      {/* Section title — ⚡ icon + "خدمات سريعة" */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <h2 className="text-2xl sm:text-3xl font-black text-white drop-shadow">{titleHeader}</h2>
        <Zap className="size-6 sm:size-7 fill-amber-400 text-amber-400 drop-shadow-[0_0_14px_rgba(251,191,36,0.6)]" />
      </div>

      {/* 3-column grid filling the width — no horizontal scroll */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {/* 1. External taxi (blue) */}
        <ServiceCard
          gradient="linear-gradient(135deg, #1e40af 0%, #2563eb 50%, #0891b2 100%)"
          blobTint="radial-gradient(circle, #38bdf8 0%, transparent 70%)"
          arrowColor="rgba(14, 116, 144, 0.95)"
          title={title_ext}
          subtitle={sub_ext}
          image={imgs.banner_external_img} Icon={Car}
          onClick={onExternalClick}
          testId="request-taxi-external-btn"
        />

        {/* 2. Internal taxi (gold) */}
        <ServiceCard
          gradient="linear-gradient(135deg, #b45309 0%, #d97706 40%, #f59e0b 100%)"
          blobTint="radial-gradient(circle, #fde047 0%, transparent 70%)"
          arrowColor="rgba(180, 83, 9, 0.95)"
          title={title_int}
          subtitle={sub_int}
          image={imgs.banner_internal_img} Icon={CarTaxiFront}
          onClick={onInternalClick}
          testId="request-taxi-internal-btn"
        />

        {/* 3. Free delivery (red/pink) */}
        <FreeDeliveryDialog
          trigger={
            <ServiceCard
              gradient="linear-gradient(135deg, #9f1239 0%, #e11d48 50%, #fb7185 100%)"
              blobTint="radial-gradient(circle, #fda4af 0%, transparent 70%)"
              arrowColor="rgba(159, 18, 57, 0.95)"
              title={title_free}
              subtitle={sub_free}
              image={imgs.banner_free_delivery_img} Icon={Bike}
              testId="request-free-delivery-btn-strip"
            />
          }
        />
      </div>
    </section>
  );
}
